/**
 * Подтверждение пакетной архивации полей (AK-750).
 *
 * Для пользователя это необратимое действие: восстановление есть, но он про
 * него не знает — поля просто пропадут из списков, с карты и из планирования.
 * Поэтому спрашиваем ДО, и спрашиваем предметно: сколько полей, сколько
 * гектаров и что будет с занятиями (архив поля уносит их с собой — так
 * работает ручка, и после заливки плана-2027 это чаще всего плановые посевы).
 *
 * Причина обязательна и пишется словами: через месяц на вопрос «почему этого
 * поля нет» отвечает только она.
 */
import { useEffect, useState } from 'react';
import { Archive, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { parseArchiveReason, type SelectionStats } from '@/pages/fields/archiveBatch';

const HA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

export function FieldsArchiveDialog({
  open,
  stats,
  subject,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  stats: SelectionStats;
  /**
   * Имя ОДНОГО поля — режим «архивирую вот это поле» (попап поля на /map,
   * AK-772). Тогда в заголовке стоит имя, а не счёт, и площадь не называется:
   * на карте числа гектаров нет (в MVT-свойствах она уже отформатирована
   * строкой), а показывать «0 га» — врать. Не передан — прежний пакетный
   * режим экрана «Поля» без изменений.
   */
  subject?: string;
  pending: boolean;
  onCancel: () => void;
  /** Причина уже разобрана и обрезана — компонент не отдаёт мусор наружу. */
  onConfirm: (reason: string) => void;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Новый вызов — новый разговор: причина от прошлой пачки к этой не относится.
  useEffect(() => {
    if (open) {
      setDraft('');
      setError(null);
    }
  }, [open]);

  const submit = () => {
    const parsed = parseArchiveReason(draft);
    if (!parsed.ok) {
      // Набранное НЕ затираем: человек видит, что написал, и правит.
      setError(parsed.error);
      return;
    }
    setError(null);
    onConfirm(parsed.value);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {subject
              ? `Архивировать поле «${subject}»?`
              : `Архивировать ${stats.count} ${stats.count === 1 ? 'поле' : 'полей'}?`}
          </DialogTitle>
          <DialogDescription>
            {subject ? (
              <>Поле исчезнет из списков, с карты и из планирования.</>
            ) : (
              <>
                {HA.format(stats.areaHa)} га. Поля исчезнут из списков, с карты и
                из планирования.
              </>
            )}
            {stats.occupied > 0 && (
              <>
                {' '}
                {subject
                  ? 'На нём есть культура в текущем сезоне — это занятие уйдёт в архив вместе с полем.'
                  : `У ${stats.occupied} из них есть культура в текущем сезоне — эти занятия уйдут в архив вместе с полем.`}
              </>
            )}{' '}
            Действие обратимо: поле можно восстановить в его карточке.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor="fields-archive-reason">Причина</Label>
          <Input
            id="fields-archive-reason"
            data-testid="fields-archive-reason"
            placeholder="например: дубль импорта"
            value={draft}
            disabled={pending}
            aria-invalid={error ? true : undefined}
            className={cn(error && 'border-destructive focus-visible:ring-destructive')}
            onChange={(e) => {
              setDraft(e.target.value);
              // Правка снимает ошибку — иначе человек заперт в красном поле.
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
          />
          {error ? (
            <span role="alert" className="text-xs text-destructive">
              {error}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">
              Останется в карточке поля — по ней потом видно, почему поля нет.
            </span>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          <Button
            variant="destructive"
            data-testid="fields-archive-confirm"
            onClick={submit}
            disabled={pending}
          >
            {pending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Архивирую…
              </>
            ) : (
              <>
                <Archive className="mr-2 h-4 w-4" />
                В архив
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
